import React from 'react';
import { vars } from '../../theme';
import HBBadgeBase, {
    HBBadgeBaseProps,
    ExtendedHBBadgeTSize
} from './HBBadgeBase';
import HBBadgeThemeIcon from './HBBadgeThemeIcon';

export type HBBadgeTypeThemed = 'AI' | 'Upgrade' | 'Campaign' | 'Beta';

export interface HBBadgeThemedProps
    extends Omit<HBBadgeBaseProps, 'bg' | 'color' | 'icon'> {
    /** When true, displays the default label text for the badge type (e.g. 'AI', 'Upgrade', 'New', 'Beta'). Overridden by `label`. */
    withLabel?: boolean;
    /** Badge theme that determines background, color, border, and icon. */
    type: HBBadgeTypeThemed;
    /** Custom label text that overrides the default for the badge type. When omitted and `withLabel` is true, the default label is shown (e.g. 'AI', 'Upgrade', 'New', 'Beta'). */
    label?: string;
    /** Badge height. 'sm' is 20px (default); 'xs' is 14px, icon-only recommended at this size. */
    size?: ExtendedHBBadgeTSize;
}

const TYPE: Record<
    HBBadgeTypeThemed,
    {
        background: string;
        color: string;
        border?: string; // can be a color or a gradient
    }
> = {
    AI: {
        background: `linear-gradient(264.24deg, rgba(246, 209, 255, 0.08) 0%, rgba(94, 48, 227, 0.048) 31%, rgba(128, 151, 243, 0.04) 70%, rgba(255, 255, 255, 0.04) 100%), ${vars.colors.surface.container}`,
        color: vars.colors.text.primary,
        border: `radial-gradient(101.99% 763.83% at 0% 0%, rgba(94, 48, 227, 0.25) 0%, rgba(185, 82, 246, 0.25) 52.5%, rgba(128, 151, 243, 0.25) 100%)`
    },
    Upgrade: {
        background: vars.colors.surface.upgrade,
        color: vars.colors.text.upgrade
    },
    Campaign: {
        background: `url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAUCAYAAADPym6aAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAZmSURBVHgBTZZpsM5lGMaf4xyyk10iuyOUEHKoUJLKMiUjQ8tMTdsHTVOjSTPap6ZpptIyGo1GH1pMSqUkayVEKWQPBwl17FuW0/2b53eGd+ayvO/zv5fruu77+ReUl5e/kFL6NlAl0DxQPdAoMCvQP3Bh4KfAc4EdgemBwYE/A98HKgfGBmYEagZKAlsDpwONA3sD6wMNAxcEygInPDczMDqwO/BL4MpADc+cCWwJ7Av0DOzyN9Ah0CDwtjlrVYo/zgbqBHoHSgOdAsdMMC/wq0keDXzsuRWBjTY9NLDdwgokhRjlgSZiYmBp4EigaWCMzwwJzA9UC7QMXJ7yp3OgX+DmQNVAYeB1mxgR2BRY4PNPBJYVTpo0CfZvsYg7Ar8HNsg+n14q0EqW6vrbw4G/AgsDtQMrA+MDh2yyt8X+KFnHJaxeoCiwxyYLzLPTuHVUBhVne36NcZvazKnAUb+HoF4E7Jayja4KfBhApf4WfCDwXmCQibfbaEuTYp0qFnZSBS+x+L02CAGHVe6EKDM+Cl3hWdQtVVls3NFaiHuZWJiyHQdKDjHuDEypZGKsUCuwOdAlUD/whQVNtOhCWZ6Qst+ZncWBYhtsKGskbqaKxP/P2K+kbKEjNviVJP4WOGh81JkbaGHzxZJJPcwm1u58Xn5menWga0EM+yfKOUeZGssGLDXw4EmbYCgZrnGBqYH25ymDNZLPFVgMA3rUgiHl2pRnjngDUp414l8auCbwtznKVQ6ScQC2HJXyXJSZZ7ixupKbRq6zYxi4SAYGGAi2ehiY71ea7KGUPXyTBGCLbSlbjnPY7ZQK/ZzyUBJ7mrm62GQ9yUIx5uW0Z9bb2CmVYnZ/SHl2+Lt7yhbDWrhiGI0MS3kjfSTDOw3EYK3yIaRsE5gS6JOyzG/Y/DGD3pCy7EtkjOaXqSBWYqkUG5chZW2vS3kx3JiyDVfK+qeBtSmvX5rEMZtUZqdEQMAfKduyBQ//m/I9ARvcCwwS+/6ulPc3CryZ8hw8H/g65blZZ2MUctbidljwbFVpaaJukoTNJtggdhzqb9Sw2WcHWmB9m5ghaYWS/ZRKsjFLPNOURvrJGgmuD3wjOytsgn8/nbL3YWOcRbBhsOVyVcBS2OWtlGesvYXdmrKtuF+wIPfCPtXeZVMUz/BiHeZlm8RirUcklCthUsqzWcPzkyVnTZGdr5bRJiZqm/K26a7E68VIlXgm0NdzDOdg4+ywGGaH2WilWqiILYplEBuy7l9O+Q6jsf0SUkP2cQdzutVcNH1GNfb7f+yOzZrDdm8TlFkITXWSqR0qwtAyWFiL9dku5Y3ygUFRY7EsXuwzMLbF891MiML3WvjjMt7A58jXWhVLzD3S71jdbKdFxj9q/pYpO6WQYaeodwNP+lBSGQpcrioM2u2yScP/qMAqGWWFM9DYab4K0UBzG2SwWQzVbBJbYreKyxAw8N+lbCPiNbbQDhLJe9XdKduuknWVSlZJJRO8lPK7FT9ssGPerwZZ3AhZ32JT/NZD9rjxmQkWBBuH96MVMj3XJmAXewyRjDHGqWtRlVWEZ9iIbLaq1oKlC81XaD0NJJQ6cU9pkUX08UEuMV5PJsr0IQutqaQEYC7w6mSLYyZe9Deee0d2l/gMH1Y0g75WEqr7fXk69xZBzHYW29PfD6jqeJtbZK3cK69KDORXwVp0elvKg3XSH7AF2+xLC2duGM45/h+LMAO8UDIntWUXO46Uxa1+d4Hx+LCSGfoaMvl5yrOyxrivmXeOxZ5UqVo2XWLjrGHulkEq2xBZmYfDMlFdVdg67HJucDbPfSm/dmClx1L2NmxPVTXi1PM5tk2RidrYSDOL5cIbaA7Y7e9zqDfPBonX0X8XS9iDKS8IZvagBDHsbNBpgc9Q5IGUB3m6Dy+1sdEe7CqbjWRok0X1tcn3PYM1mpuY4cVy96S8aqub+KiNcra1RWG7PsbnM9N6FtjwagnZlrLFIWirsY4be2yRcnGzYgs2Red0biPVMTF3RVtV2CNrfI9V2CqVZRRbITs2xe8MNbPGNqrtmYqVyxywBOZazCwbuT9lS9N4qeeZ3yOSt9yad/s9r08LaORZi8RK3NTldl4tnZuZikGvkBNphxu8tw2xTrmkRsk0N3mRcfkbW/VSMdYrG6lMsjbaLAuE+WDrXS0Z1VTssE0cs4m95kO1sv8BKtTvsKuys2MAAAAASUVORK5CYII='), ${vars.colors.base.pink}`,
        color: vars.colors.text.primary
    },
    Beta: {
        background: vars.colors.surface.neutral,
        color: vars.colors.text.secondary
    }
};

const defaultLabels: Record<HBBadgeTypeThemed, string> = {
    AI: 'AI',
    Upgrade: 'Upgrade',
    Campaign: 'New',
    Beta: 'Beta'
};

const HBBadgeThemed: React.FC<HBBadgeThemedProps> = ({
    size = 'sm',
    type = 'AI',
    withLabel = true,
    label,
    ...props
}) => {
    return (
        <HBBadgeBase
            size={size}
            bg={TYPE[type].background}
            color={TYPE[type].color}
            border={TYPE[type].border}
            icon={() => (
                <HBBadgeThemeIcon size={size === 'sm' ? 12 : 10} type={type} />
            )}
            label={label || (withLabel && defaultLabels[type])}
            {...props}
        />
    );
};

export default HBBadgeThemed;
